import classnames from "classnames";
import { debounce } from "lodash";
import { useReducer, useCallback, useEffect } from "react";
import { withTranslation } from "react-i18next";

import { useFormatCurrency } from "@krea/customer-features/hooks";
import { settings } from "@krea/customer-features/settings";
import TextInput from "@krea/customer-features/shared-components/fields/input";
import { usePostFieldRequest } from "@krea/customer-features/store/field-requests/hooks/mutations";
import { useGetOneFieldRequest } from "@krea/customer-features/store/field-requests/hooks/queries";

import FieldRequestPreviewHeader from "../FieldRequestPreviewHeader";
import FieldRequestSaved from "../FieldRequestSaved";

const AmountForm = ({
  fieldRequestID,
  isPreviewMode,
  isThreeStepsProcess,
  t,
  withInputLabel,
}) => {
  const { data: { submittedData, field, displayName, displayText } = {} } =
    useGetOneFieldRequest(fieldRequestID);
  const { mutate: postFieldRequest, error: err } =
    usePostFieldRequest(fieldRequestID);

  const error = err?.data.errors[0]?.message;

  function reducer(state, action) {
    switch (action.type) {
      case "value":
        return { ...state, value: action.payload };
      case "touched":
        return { ...state, touched: action.payload };
      case "changeAmount":
        return {
          ...state,
          value: Number.isInteger(action.payload) ? action.payload : "",
          touched: state.value !== action.payload,
        };

      default:
        throw new Error();
    }
  }

  const [state, setState] = useReducer(reducer, {
    value: !Number.isNaN(parseInt(submittedData))
      ? parseInt(submittedData)
      : "",
    touched: false,
  });

  const { value } = state;

  const currency = settings.currency;
  const [formattedCurrencyValue, currencyValue] = useFormatCurrency({
    currency,
  });

  const onBlur = (state) => {
    const { touched, value } = state;

    if (touched) {
      setState({ type: "touched", payload: false });
      postFieldRequest({ value: value === "" ? null : value });
    }
  };

  const onChange = ({ target }) => {
    const value = parseInt(target.value.replace(/\s+/g, ""), 10);
    setState({ type: "changeAmount", payload: value });
  };

  const onKeyDown = (e, state) => {
    const { value } = state;

    if (e.key === "Enter") {
      e.preventDefault();
      postFieldRequest({ value: value === "" ? null : value });
    }
  };

  const debouncedFunc = debounce((state) => onBlur(state), 1500);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSave = useCallback((state) => debouncedFunc(state), []);

  useEffect(() => {
    debouncedSave(state);

    return () => {
      debouncedFunc.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  const formClasses = classnames("input-form", {
    "input-form-padded": isPreviewMode,
  });

  return (
    <>
      {isPreviewMode ? (
        <FieldRequestPreviewHeader
          label={displayName}
          value={
            (submittedData || submittedData === 0) &&
            formattedCurrencyValue(value)
          }
        />
      ) : (
        <form className={formClasses} onKeyDown={(e) => onKeyDown(e, state)}>
          <TextInput
            autoFocus={!isThreeStepsProcess}
            type="text"
            pattern="[0-9]*"
            maxLength="15"
            inputMode="numeric"
            id={`input_${field}`}
            value={value.toLocaleString("sv")}
            onChange={onChange}
            onBlur={() => onBlur(state)}
            placeholder={t("iaq.amount.valuePlaceholder")}
            textAppend={currencyValue}
            label={withInputLabel && displayName}
            description={displayText}
            error={error}
          />
          <FieldRequestSaved fieldRequestID={fieldRequestID} />
        </form>
      )}
    </>
  );
};

export default withTranslation()(AmountForm);
